/**
 * ConfirmProvider + useConfirm — confirmation in-app impérative et globale.
 *
 * Remplace window.confirm / confirm(), interdits car ils gèlent la WebView
 * Capacitor sur Sunmi (incident #171). Un seul AlertDialog monté à la racine.
 *
 * Usage :
 *   const confirm = useConfirm();
 *   if (!(await confirm({ title: 'Supprimer ?', destructive: true }))) return;
 */
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

export type ConfirmOptions = {
  title: string;
  /** Corps optionnel — les retours à la ligne (\n) sont préservés. */
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Style destructif (rouge) sur le bouton de confirmation. */
  destructive?: boolean;
};

type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

// Pont impératif : permet aux services hors-React (ex. WebPayment) de confirmer
// sans window.confirm. Alimenté par le provider monté. Fallback sûr = refus.
let imperativeConfirm: ConfirmFn | null = null;

/** Confirmation utilisable hors composant React. Renvoie false si aucun provider monté. */
export function confirmImperative(options: ConfirmOptions): Promise<boolean> {
  return imperativeConfirm ? imperativeConfirm(options) : Promise.resolve(false);
}

export function ConfirmProvider({ children }: { children: React.ReactNode }) {
  const [options, setOptions] = useState<ConfirmOptions | null>(null);
  const resolverRef = useRef<((value: boolean) => void) | null>(null);

  const confirm = useCallback<ConfirmFn>((opts) => {
    setOptions(opts);
    return new Promise<boolean>((resolve) => {
      resolverRef.current = resolve;
    });
  }, []);

  const settle = useCallback((value: boolean) => {
    resolverRef.current?.(value);
    resolverRef.current = null;
    setOptions(null);
  }, []);

  useEffect(() => {
    imperativeConfirm = confirm;
    return () => {
      imperativeConfirm = null;
    };
  }, [confirm]);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <AlertDialog open={!!options} onOpenChange={(o) => !o && settle(false)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{options?.title}</AlertDialogTitle>
            {options?.description && (
              <AlertDialogDescription className="whitespace-pre-line">
                {options.description}
              </AlertDialogDescription>
            )}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => settle(false)}>
              {options?.cancelLabel ?? 'Annuler'}
            </AlertDialogCancel>
            <AlertDialogAction
              className={
                options?.destructive
                  ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90'
                  : undefined
              }
              onClick={() => settle(true)}
            >
              {options?.confirmLabel ?? 'Confirmer'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ConfirmContext.Provider>
  );
}

export function useConfirm(): ConfirmFn {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm doit être utilisé dans un <ConfirmProvider>');
  return ctx;
}
